import { Flex, SkipNavContent, Spacer } from '@homebotapp/hb-react-component-catalog'
import { addMonths, isAfter, startOfMonth } from 'date-fns'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { BombBomb } from '../../../App/BombBomb'
import { HomeNavigation } from '../../../App/HomeNavigation'
import { HomeownerIntro } from '../../../Components/homeowners/HomeownerIntro'
import { setReferrerSource, trackVideoMailerView } from '../../../actions/trackingSource'
import { ROUTE_KEY_HOME } from '../../../constants/routes'
import { HomeownerDigestTrackingEventLocation } from '../../../constants/tracking'
import { useOpeningVideo } from '../../../hooks/useOpeningVideo'
import useViewport from '../../../hooks/useViewport'
import { selectClientFetchId, selectClientHomes } from '../../../store/selectors/client'
import {
  selectCustomerLegacyRef,
  selectDisableGetLeadsFeature,
  selectHomebotListingsFeatureEnabled,
  selectNativeAccessFeature
} from '../../../store/selectors/customerProfile'
import { selectHomeDigestLoading, selectShouldRenderRefiOptions } from '../../../store/selectors/display'
import { selectHomeId, selectHomeUuid } from '../../../store/selectors/home'
import { selectPaidInFull } from '../../../store/selectors/home/answers'
import { selectFirstCEL } from '../../../store/selectors/home/loans'
import { selectShowReverseMortgage } from '../../../store/selectors/home/reverseMortgage'
import { fetchShowPmiModules } from '../../../store/slices/pmi'
import { IDLE, LoadingStatus } from '../../../types/loadingStatus'
import { RootState } from '../../../types/rootState'
import { AvmModule } from '../../shared/AvmModule/AvmModule'
import { Dashboard } from '../../shared/Dashboard'
import { ExtraPaymentsModule } from '../../shared/ExtraPaymentsModule/ExtraPaymentsModule'
import { Footer } from '../../shared/Footer'
import ImportantNow from '../../shared/ImportantNow'
import { DigestNativeAppAdModule } from '../../shared/DigestNativeAppAdModule/DigestNativeAppAdModule'
import OpeningVideo from '../../shared/OpeningVideo'
import { PrincipalAndInterestModule } from '../../shared/PrincipalAndInterestModule'
import { RefiModule } from '../../shared/RefiModule/RefiModule'
import ShareCta from '../../shared/ShareCta'
import CashOut from '../CashOut'
import HomeInsurance from '../HomeInsurance'
import { HomeownerAirDna } from '../HomeownerAirDna'
import { LoansModal } from '../LoansModal/LoansModal'
import { NoLoansModule } from '../NoLoansModule'
import { PmiEstimatedModule } from '../PmiEstimatedModule'
import { PmiProvidedModule } from '../PmiProvidedModule'
import PurchasingPower from '../PurchasingPower'
import { ReverseMortgageModule } from '../ReverseMortgageModule'
import { HomeownerShortcutsDrawer } from '../HomeownerShortcutsDrawer/HomeownerShortcutsDrawer'
import { HomeownerSidebar } from '../HomeownerSidebar/HomeownerSidebar'
import { selectBombBombIsEnabled } from '../../../store/slices/bombBomb'
import { selectShouldShowAirDnaModule } from '../../../store/selectors/airDna'
import { AvmModuleProvider } from '../../shared/AvmModule/AvmModuleContext'
import { OwnCustomModule } from '../OwnCustomModule/OwnCustomModule'
import styles from './HomeownerDashboard.module.scss'
import { HideOnFlag } from '../../shared/HideOnFlag'
import { ClientEventName, ClientEventSource } from '../../../api/gqlaxy/gql/generated/graphql'
import { selectReferrerSource } from '../../../store/selectors/trackingSource'
import { useCreateHomeClientEventOnce } from '../../../hooks'
import { useCreateHomeClientEvent } from '../../../hooks/useCreateHomeClientEvent'
import { GalleryModule } from '../GalleryModule/GalleryModule'

export const HomeownerDashboard = () => {
  const dispatch = useDispatch()
  const homes = useSelector(selectClientHomes)
  const homeUuid = useSelector(selectHomeUuid)
  const paidInFull = useSelector(selectPaidInFull)
  const clientId = useSelector(selectClientFetchId)
  const referrerSource = useSelector(selectReferrerSource)
  const hasRefiOptions = useSelector(selectShouldRenderRefiOptions)
  const hasReverseMortgageEnabledAndEquityOverFiftyPercentAndAgeGreaterThanSixtyTwo =
    useSelector(selectShowReverseMortgage)
  const showAirDnaModule = useSelector(selectShouldShowAirDnaModule)
  // This ensures we get the correct date when formatting (crazy JS date nuances): https://stackoverflow.com/questions/7556591/is-the-javascript-date-object-always-one-day-off
  const loanOriginationDate = useSelector(selectFirstCEL)?.date?.split('-').join('/')
  const firstPaymentDate = addMonths(startOfMonth(new Date(loanOriginationDate)), 2)
  const hasValidLoan = isAfter(new Date(), firstPaymentDate) && !paidInFull
  const fetchShowPmiModulesStatus = useSelector<RootState, LoadingStatus>(state => state.pmi.fetchShowPmiModulesStatus)
  const hasNativeAccess = useSelector<RootState, boolean>(selectNativeAccessFeature)
  const bombBombIsEnabled = useSelector(selectBombBombIsEnabled)
  const searchEnabled = useSelector(selectHomebotListingsFeatureEnabled)
  // Lots of race condition issues require me to
  // know when loading is done to start animations on
  // the shortcuts drawer.
  const isLoading = useSelector(selectHomeDigestLoading)
  const disableGetLeadsFeature = useSelector(selectDisableGetLeadsFeature)

  const {
    displayWelcomeVideo,
    displayEmailVideo,
    videoType: videoSource,
    videoId,
    audience: audienceType
  } = useOpeningVideo()

  const displayOpeningVideo = displayEmailVideo || displayWelcomeVideo

  const [tracked, setTracked] = useState([])

  const homeId = useSelector(selectHomeId)
  const agentId = useSelector(selectCustomerLegacyRef)
  // @ts-ignore
  const shouldTrack = agentId && homeId && !isLoading && !tracked.includes(homeId)
  const shouldTrackVideoView = displayEmailVideo && shouldTrack

  const createViewedVideoEmailClientEvent = useCreateHomeClientEvent({
    eventName: ClientEventName.ViewedVideoEmail,
    eventProperties: {
      activity_source: referrerSource,
      audience: audienceType,
      video_id: videoId,
      video_source: videoSource
    }
  })

  if (shouldTrackVideoView) {
    const eventProperties = {
      audienceType,
      videoId,
      videoSource
    }

    dispatch(trackVideoMailerView(eventProperties, createViewedVideoEmailClientEvent))
  }

  const { isLargeOrAbove } = useViewport()
  const createViewedHomeClientEvent = useCreateHomeClientEventOnce({
    eventName: ClientEventName.View,
    eventSource: ClientEventSource.Digest,
    eventProperties: {
      activity_source: referrerSource
    }
  })

  useEffect(() => {
    if (shouldTrack) {
      createViewedHomeClientEvent()
      dispatch(setReferrerSource(null))
      // @ts-ignore
      setTracked([...tracked, homeId])
    }
  }, [homeId, shouldTrack, dispatch, tracked])

  useEffect(() => {
    if (clientId && homeUuid && fetchShowPmiModulesStatus === IDLE) {
      dispatch(fetchShowPmiModules({ clientId, homeUuid }))
    }
  }, [dispatch, clientId, homeUuid, fetchShowPmiModulesStatus])

  return (
    <>
      {isLargeOrAbove && <HomeownerSidebar />}
      <div className={styles.stage}>
        <SkipNavContent scrollMarginTop='var(--digest-module-scroll-margin-top)' />
        <Dashboard data-qa='HomeownersDashboardRight' className={styles.dashboard}>
          <Flex>
            {bombBombIsEnabled && <BombBomb />}
            <Spacer />
            {homes?.length > 1 && <HomeNavigation display={['none', null, null, 'flex']} />}
          </Flex>

          <HomeownerIntro />
          {displayOpeningVideo && <OpeningVideo />}
          <LoansModal />
          <AvmModuleProvider>
            <AvmModule />
          </AvmModuleProvider>
          {searchEnabled && <GalleryModule />}
          <OwnCustomModule />
          <NoLoansModule />
          <ImportantNow />
          {hasNativeAccess && (
            <DigestNativeAppAdModule
              display={['block', null, null, 'none']}
              tracking={{
                guid: 'fJh8JupjdZtUFwA91BUs6e',
                ui_context: HomeownerDigestTrackingEventLocation.HOMEOWNER_DIGEST_NATIVE_APP_AD_MODULE
              }}
            />
          )}
          <PmiEstimatedModule />
          <PmiProvidedModule />
          {hasReverseMortgageEnabledAndEquityOverFiftyPercentAndAgeGreaterThanSixtyTwo && <ReverseMortgageModule />}
          {hasValidLoan && <PrincipalAndInterestModule withCurrentHomeData />}
          {hasValidLoan && <ExtraPaymentsModule withCurrentHomeData />}
          {hasRefiOptions && hasValidLoan && <RefiModule withCurrentHomeData />}
          <PurchasingPower />
          <CashOut />
          <HomeInsurance />
          {showAirDnaModule && <HomeownerAirDna />}
          <HideOnFlag flag={disableGetLeadsFeature}>
            <ShareCta />
          </HideOnFlag>
          <Footer reportType={ROUTE_KEY_HOME} className={styles.homeownerFooter} />
          {!isLargeOrAbove && <HomeownerShortcutsDrawer />}
        </Dashboard>
      </div>
    </>
  )
}
